'use client';

import React, { useState } from 'react';

interface SignUpSideBySideProps {
  heading: string;
  buttonText: string;
  emailPlaceholder: string;
  privacyText: string;
  privacyPolicyText: string;
  privacyPolicyHref: string;
}

export default function SignUpSideBySide({
  heading,
  buttonText,
  emailPlaceholder,
  privacyText,
  privacyPolicyText,
  privacyPolicyHref
}: SignUpSideBySideProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }

      setSubmitStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Newsletter submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative isolate bg-teal-950 py-16 sm:py-24 lg:py-32">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <svg
          viewBox="0 0 1024 1024"
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 size-[64rem] -translate-x-1/2"
        >
          <circle r={512} cx={512} cy={512} fill="url(#759c1415-0410-454c-8f7c-9a820de03641)" fillOpacity="0.5" />
          <defs>
            <radialGradient
              r={1}
              cx={0}
              cy={0}
              id="759c1415-0410-454c-8f7c-9a820de03641"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(512 512) rotate(90) scale(512)"
            >
              <stop stopColor="#084751" />
              <stop offset={1} stopColor="#00CC66" stopOpacity={0} />
            </radialGradient>
          </defs>
        </svg>
      </div>
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <h2 className="max-w-xl mx-auto lg:mx-0 heading-2 text-white lg:col-span-7 text-center lg:text-left">
          {heading}
        </h2>
        <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto lg:mx-0 lg:col-span-5 lg:pt-2">
          <div className="flex gap-x-4">
            <label htmlFor="email-address" className="sr-only">
              Email address
            </label>
            <input
              id="email-address"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={emailPlaceholder}
              autoComplete="email"
              className="min-w-0 flex-auto rounded-md bg-white/10 px-3.5 py-2 body-text text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-white/50 focus:outline-2 focus:-outline-offset-2 focus:outline-teal-950"
              aria-invalid={submitStatus === 'error'}
              aria-describedby={submitStatus === 'error' ? 'newsletter-error' : undefined}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-none rounded-md bg-happy-600 px-3.5 py-2.5 body-text font-semibold text-teal-950 shadow-xs hover:bg-white hover:text-teal-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-950 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : buttonText}
            </button>
          </div>
          {submitStatus === 'error' && (
            <p id="newsletter-error" className="mt-2 text-sm text-red-400">
              {errorMessage}
            </p>
          )}
          {submitStatus === 'success' && (
            <p className="mt-2 text-sm text-happy-400">
              Thank you for signing up!
            </p>
          )}
          <p className="mt-4 body-text text-[14px] text-white/60 leading-6">
            {privacyText}{' '}
            <a href={privacyPolicyHref} className="font-semibold text-white/80">
              {privacyPolicyText}
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  )
}
  